/**
 * Host Serializer
 *
 * Centralized serialization of host/connection metadata for workflow definitions.
 * Each connection type (ServiceProvider, ApiConnection, etc.) requires a
 * different host shape in the workflow JSON. This module is the single source
 * of truth for those shapes.
 */

/** Supported connection types for host serialization */
export type ConnectionType =
  | 'ApiConnection'
  | 'ApiConnectionNotification'
  | 'ApiConnectionWebhook'
  | 'ServiceProvider'
  | 'Workflow'
  | 'Function'
  | 'Agent'
  | 'MCP';

export interface HostSerializationInput {
  /** Connection type */
  connectionType: ConnectionType | (string & {});
  /** The connection reference name or key */
  connectionKey: string;
  /** Service provider ID (e.g., '/serviceProviders/sql') — for ServiceProvider type */
  serviceProviderId?: string | undefined;
  /** Operation ID (e.g., 'getRows') — for ServiceProvider or create-path ApiConnection */
  operationId?: string | undefined;
  /** API ID (e.g., '/managedApis/sql') — for create-path ApiConnection */
  apiId?: string | undefined;
}

/**
 * Serialize host/connection metadata into the correct shape for workflow definitions.
 *
 * Returns:
 * - ServiceProvider: `{ serviceProviderConfiguration: { serviceProviderId, operationId, connectionName } }`
 * - ApiConnection / Notification / Webhook: `{ connection: { referenceName } }` with optional apiId/operationId
 * - Workflow: `{ workflow: { id } }`
 * - Function: `{ function: { connectionName } }`
 * - Agent / unknown: `null`
 * - MCP: `{ mcpConfiguration: { connectionName } }`
 *
 * For ServiceProvider, the result contains a `serviceProviderConfiguration` key —
 * use `applyHostSerialization()` to route it to the correct field on inputs.
 */
export function serializeHost(input: HostSerializationInput): Record<string, unknown> | null {
  const { connectionType, connectionKey } = input;

  switch (connectionType) {
    case 'ServiceProvider':
      return {
        serviceProviderConfiguration: {
          serviceProviderId: input.serviceProviderId ?? '',
          operationId: input.operationId ?? '',
          connectionName: connectionKey,
        },
      };

    case 'ApiConnection':
    case 'ApiConnectionNotification':
    case 'ApiConnectionWebhook': {
      const host: Record<string, unknown> = {};
      if (input.apiId !== undefined) host.apiId = input.apiId;
      if (input.operationId !== undefined) host.operationId = input.operationId;
      host.connection = { referenceName: connectionKey };
      return host;
    }

    case 'Workflow':
      return {
        workflow: { id: connectionKey },
      };

    case 'Function':
      return {
        function: { connectionName: connectionKey },
      };

    case 'Agent':
      return null;

    case 'MCP':
      return {
        mcpConfiguration: { connectionName: connectionKey },
      };

    default:
      return null;
  }
}

/**
 * Apply a serialized host result to an inputs object.
 *
 * Routes the result to the correct field:
 * - ServiceProvider results → `inputs.serviceProviderConfiguration`
 * - All others → `inputs.host`
 *
 * When `merge` is true, existing host properties are preserved (spread).
 */
export function applyHostSerialization(
  inputs: Record<string, unknown>,
  serialized: Record<string, unknown> | null,
  options?: { merge?: boolean },
): void {
  if (!serialized) return;

  if ('serviceProviderConfiguration' in serialized) {
    inputs.serviceProviderConfiguration = serialized.serviceProviderConfiguration;
  } else if ('mcpConfiguration' in serialized) {
    inputs.mcpConfiguration = serialized.mcpConfiguration;
  } else if (options?.merge) {
    inputs.host = { ...(inputs.host as Record<string, unknown> || {}), ...serialized };
  } else {
    inputs.host = serialized;
  }
}
